import {
  Component, CSSProperties, FormEvent, ReactNode, createContext,
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TablerIcon } from '@tabler/icons-react';
import { IconChevronRight, IconMaximize, IconSchool, IconStar, IconTrophy, IconCake } from '@tabler/icons-react';
import {
  BALL_PARTS, KIco, KLaurel, KSlideKind, SLIDE_ICON, subjectIcon,
} from './kioskIcons';
import {
  BannerContent, BannerDesign, BannerLang, BannerTemplate, TEMPLATES, mergeDesign, pickLang,
} from '../lib/bannerDesign';
import { PoweredBy } from '../components/PoweredBy';
import './kiosk.css';

/**
 * KIOSK — maktab dahlizidagi katta TV ekrani (24/7 ishlaydi).
 *
 * OCHIQ sahifa: /kiosk?key=<kioskKey>. Login talab qilmaydi — shuning uchun
 * services/api.ts dagi axios namunasi ISHLATILMAYDI (uning interceptor'i 401 da
 * /login ga otib yuboradi). Faqat oddiy fetch.
 *
 * Ekran: Reyting TOP-10 → Sinf yetakchilari → Bugungi dars jadvali → Bannerlar,
 * aylanma (12 soniyada bir slayd). Ma'lumot har 60 soniyada yangilanadi;
 * tarmoq uzilsa oxirgi ma'lumot ekranda qoladi.
 */

const API_URL = import.meta.env.VITE_API_URL || 'https://sata.up.railway.app';
const KEY_LS = 'sata_kiosk_key';
const POLL_MS = 60_000;
/** Bitta so'rov uchun eng ko'p kutish — osilib qolgan TCP ulanishi yangilanishni to'xtatmasin. */
const FETCH_TIMEOUT_MS = 20_000;
/** Render xatosidan so'ng sahifani avtomatik qayta yuklash. */
const CRASH_RELOAD_MS = 60_000;
/**
 * Slayd davomiyligi TURIGA qarab: zich jadvalni o'qish uchun ko'proq vaqt kerak,
 * bitta bannerni esa 14 soniya tikilib turish zerikarli. Bu ISHONCHLILIK mantiqi
 * EMAS — poll/timeout/seq/inFlight tegilmaydi, faqat aylanma taymeri va progress
 * yo'lakchasining davomiyligi shu jadvaldan olinadi.
 */
const SLIDE_MS: Record<KSlideKind, number> = {
  top: 13_000,
  birthdays: 12_000,
  leaders: 12_000,
  classActivity: 11_000,
  schedule: 14_000,
  weekStar: 11_000,
  banner: 10_000,
};
/** Slaydlar hali tuzilmaganda (yuklanish) ishlatiladigan neytral qiymat. */
const SLIDE_MS_DEFAULT = 12_000;
const LEADERS_PER_SLIDE = 12;
const CLASSES_PER_SLIDE = 5;
/** Bitta "Tug'ilgan kunlar" slaydiga sig'adigan eng ko'p karta (undan ortig'i sahifalanadi). */
const BIRTHDAYS_PER_SLIDE = 8;
/** Ball sanog'i (count-up) — kiosk.css `--k-dur-count` / `--k-delay-count` bilan bir xil. */
const COUNT_MS = 900;
const COUNT_DELAY_MS = 260;
/** `kRise` pog'onasi 12 tugundan oshmasin: oxirgi element 495ms da boshlanadi. */
const RISE_MAX_STEP = 11;
/**
 * `kRise` davomiyligi — kiosk.css `--k-dur-rise` bilan BIR XIL bo'lishi shart.
 * Qahramon kartada kirish tugagach "nafas" (kBreath) yoqiladi (qarang: `KHero`).
 */
const RISE_MS = 420;
/**
 * BITTA SLAYDDA ko'rsatiladigan eng ko'p VAQT ORALIG'I (umumiy vaqt o'qidagi qator).
 * Bu chegara endi KESISH emas — undan uzun vaqt o'qi slaydlarga BO'LINADI.
 * 10 emas, 8: 1080p da 10 qatorda katak balandligi ~54px ga tushadi va matn
 * pastki qismidan qirqila boshlaydi; 8 qator o'qilarli chegara.
 */
const MAX_SLOTS = 8;
/**
 * Vaqt o'qi bo'yicha eng ko'p sahifa. Ma'lumot buzilsa (masalan yuzlab har xil
 * boshlanish vaqti) aylanma cheksiz slaydga to'lib ketmasin. Shu chegaradan
 * oshgani ekranda ANIQ ko'rsatkich bilan aytiladi (`.k-more`).
 * 8 x 4 = 32 vaqt oralig'i — hech bir maktabda bunchasi bo'lmaydi.
 */
const MAX_TIME_PAGES = 4;
/** Shu qadar (va undan ko'p) vaqt oralig'idan boshlab jadval "zich" rejimga o'tadi. */
const DENSE_SLOTS = 7;
const TZ = 'Asia/Tashkent';

// ── Kontrakt turlari (GET /kiosk/screen) ───────────────────────────────────
interface RankItem {
  rank: number;
  fullName: string;
  className: string | null;
  ball: number;
  photo: string | null;
}
interface ClassLeader { className: string; item: RankItem }
interface KioskClass { id: string; name: string }
interface KioskEntry {
  classId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  room: string | null;
  subjectName: string;
  teacherName: string | null;
}
interface KioskBanner {
  id: string;
  template: BannerTemplate;
  content: BannerContent | null;
  design: BannerDesign | null;
}
/** Bugun tug'ilgan kuni bo'lgan o'quvchi (Toshkent kuni bo'yicha). */
interface KioskBirthday {
  fullName: string;
  className: string | null;
  photo: string | null;
  /** Nechchi yoshga to'ldi. Sana noto'g'ri kiritilgan bo'lsa — `null`. */
  turns: number | null;
}
/** Sinf faolligi — "Eng faol sinflar" diagrammasi qatori. */
interface KioskClassActivity { className: string; avg: number; count: number }
/** 7 kunlik oyna g'olibi. */
interface KioskWeekStar {
  fullName: string;
  className: string | null;
  photo: string | null;
  ball: number;
  breakdown: {
    grades: number | null; attendance: number | null; behavior: number | null;
    exams: number | null; homework: number | null;
  };
}
interface KioskScreen {
  school: { name: string };
  serverTime: string;
  todayDow: number;
  period: { days: number };
  ranking: { top: RankItem[]; classLeaders: ClassLeader[] };
  /**
   * Yangi maydonlar ATAYLAB ixtiyoriy: yangilangan front eski backend bilan ham
   * ishlashi kerak (TV o'zi yangilanadi, server esa keyinroq deploy bo'lishi
   * mumkin). Ma'lumot yo'q bo'lsa mos slayd umuman yaratilmaydi.
   */
  birthdays?: KioskBirthday[];
  classActivity?: KioskClassActivity[];
  weekStar?: KioskWeekStar | null;
  schedule: { classes: KioskClass[]; entries: KioskEntry[] };
  banners: KioskBanner[];
}

// ── Slaydlar ───────────────────────────────────────────────────────────────
interface ScheduleCol { cls: KioskClass; lessons: KioskEntry[] }
type Slide =
  | { key: string; kind: 'top'; items: RankItem[]; periodDays: number }
  | { key: string; kind: 'birthdays'; items: KioskBirthday[]; page: number; pages: number; dateLabel: string }
  | {
    key: string; kind: 'leaders'; items: ClassLeader[]; page: number; pages: number;
    /**
     * Ball yo'lakchasining MAXRAJI — maktabdagi eng yuqori ball (kamida 100).
     * Slaydlar tuzilayotganda hisoblanadi, chunki faqat o'sha yerda TOP-10 ham
     * qo'lda bo'ladi: sahifalangan yetakchilarning o'zidan olingan maksimum har
     * sahifada boshqacha chiqib, yo'lakchalarni SOLISHTIRIB bo'lmas qilardi.
     */
    scale: number;
  }
  | { key: string; kind: 'classActivity'; items: KioskClassActivity[]; periodDays: number }
  | { key: string; kind: 'weekStar'; star: KioskWeekStar }
  | {
    key: string; kind: 'schedule'; dow: number; isToday: boolean; cols: ScheduleCol[];
    /** Umumiy vaqt o'qi: shu SLAYDDA ko'rsatiladigan boshlanish vaqtlari (o'sish tartibida). */
    slots: string[];
    /** Vaqt o'qi nechta slaydga bo'lingani (1 — bo'linmagan, ya'ni kun to'liq sig'gan). */
    timePages: number;
    /** MAX_TIME_PAGES ga ham sig'magan — ya'ni UMUMAN ko'rsatilmaydigan vaqt qatorlari. */
    hiddenSlots: number;
    page: number; pages: number;
  }
  | { key: string; kind: 'banner'; banner: KioskBanner; page: number; pages: number };

/** Jadval slaydi (sahifa raqamlari barcha slaydlar tuzilgach to'ldiriladi). */
type ScheduleSlideData = Extract<Slide, { kind: 'schedule' }>;

/**
 * Pastki paneldagi "Keyingi: …" yorlig'i. Bola nima kelishini bilsa — KUTADI;
 * bu butun ekrandagi eng arzon va eng kuchli ushlab turish vositasi.
 */
const SLIDE_LABEL: Record<KSlideKind, string> = {
  top: 'TOP-10 reytingi',
  birthdays: "Tug'ilgan kunlar",
  leaders: 'Sinf yetakchilari',
  classActivity: 'Eng faol sinflar',
  schedule: 'Dars jadvali',
  weekStar: 'Hafta yulduzi',
  banner: "E'lonlar",
};

/**
 * SINF rang kodining 3 tonli ko'k gammasi. RANG QOIDASI: rang FAQAT yonma-yon
 * turgan BIRODAR elementlarni ajratadi (qaysi ustun/karta qaysi sinfniki?),
 * SHAXSNI belgilamaydi. Shu sabab u UCH joyda ishlatiladi va uchalasida ham
 * SINFGA biriktirilgan: dars jadvali ustuni sarlavhasi, eng faol sinf qatori,
 * sinf yetakchisi va tug'ilgan kun kartalarining sarlavha bandi
 * (hammasi `.k-card-head`).
 * Ketma-ketlik a·b·c·a·b: 5 ustunda ham qo'shnilar hech qachon bir xil emas.
 * Uchala ton ustida OQ matn turadi (4.84 / 6.41 / 7.42:1 — WCAG AA ✓).
 */
const COL_FILL = ['var(--k-col-a)', 'var(--k-col-b)', 'var(--k-col-c)'];
/** Sarlavha OSTIDAGI yo'lakcha — unda MATN yo'q, shuning uchun och tonlar mumkin. */
const COL_RAIL = ['var(--k-rail-a)', 'var(--k-rail-b)', 'var(--k-rail-c)'];

// ── O'zbekcha sana ─────────────────────────────────────────────────────────
const MONTHS = [
  'yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun',
  'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr',
];
/** 1=Dushanba .. 7=Yakshanba (backend todayDow bilan bir xil). */
const DAYS: Record<number, string> = {
  1: 'Dushanba', 2: 'Seshanba', 3: 'Chorshanba', 4: 'Payshanba',
  5: 'Juma', 6: 'Shanba', 7: 'Yakshanba',
};

const SHORT_DOW: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

/** Toshkent vaqti — TV soati noto'g'ri bo'lsa ham to'g'ri ko'rsatish uchun. */
const TZ_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  hour12: false,
  weekday: 'short',
  day: 'numeric',
  month: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

interface TzParts { h: number; m: number; s: number; day: number; month: number; dow: number }

function tzParts(ms: number): TzParts {
  const map: Record<string, string> = {};
  for (const p of TZ_FMT.formatToParts(new Date(ms))) map[p.type] = p.value;
  return {
    h: Number(map.hour) % 24, // ba'zi brauzerlarda yarim tunda "24" qaytadi
    m: Number(map.minute) || 0,
    s: Number(map.second) || 0,
    day: Number(map.day) || 1,
    month: (Number(map.month) || 1) - 1,
    dow: SHORT_DOW[map.weekday] ?? 1,
  };
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const roundBall = (n: number) => Math.round(Number(n) || 0);

/**
 * Ball rangining MA'NOSI — ilovadagi qoida bilan bir xil
 * (app/src/screens/RankingScreen.tsx `pctColor`): rang O'RINGA emas, QIYMATGA
 * qarab beriladi (reyting balli 0..100 shkalasida). Ilgari kiosk faqat 1-o'rinni
 * ko'k qilardi — bu ilovadagi "70 dan yuqori — yaxshi, 50 dan past — past"
 * semantikasini butunlay yo'qotardi.
 * TV uchun ohanglar bir pog'ona to'q (kiosk.css: --k-brand-dark / --k-amber-tv /
 * --k-coral-tv) — app'ning amber #F5A524 va coral #F2445B tonlari oq fonda
 * 2.04:1 va 3.65:1 beradi, ya'ni 5 metrdan o'qilmaydi.
 */
function ballTone(ball: number): string {
  const v = Number(ball) || 0;
  if (v >= 70) return 'is-hi';
  if (v >= 50) return 'is-mid';
  return 'is-low';
}

/** `#RRGGBB` → `rgba(...)`. Rangni admin kiritadi — noto'g'ri qiymatda `null`. */
function hexAlpha(hex: string | undefined, alpha: number): string | null {
  const m = /^#([0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** "08:30" → 510 daqiqa. Noto'g'ri qiymatda -1. */
function toMinutes(t: string): number {
  const m = /^(\d{1,2}):(\d{2})/.exec(t || '');
  if (!m) return -1;
  return Number(m[1]) * 60 + Number(m[2]);
}

function initials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[1][0] || '' : '')).toUpperCase();
}

/** Kiosk fayl havolasi. `sticker:` avatarlar fayl emas — rasm so'ralmaydi. */
function kioskFileUrl(key: string | null | undefined, kioskKey: string): string | undefined {
  if (!key || key.startsWith('sticker:')) return undefined;
  return `${API_URL}/kiosk/file/${key}?key=${encodeURIComponent(kioskKey)}`;
}

function readStoredKey(): string {
  const fromUrl = new URLSearchParams(window.location.search).get('key');
  if (fromUrl) return fromUrl.trim();
  try {
    return localStorage.getItem(KEY_LS) || '';
  } catch {
    return '';
  }
}

/** Bugun dars bo'lmasa — keyingi darsli kunni (aylanma tartibda) topadi. */
function pickDow(entries: KioskEntry[], todayDow: number): number | null {
  if (!entries.length) return null;
  const start = todayDow >= 1 && todayDow <= 7 ? todayDow : 1;
  for (let i = 0; i < 7; i++) {
    const d = ((start - 1 + i) % 7) + 1;
    if (entries.some((e) => e.dayOfWeek === d)) return d;
  }
  return null;
}

// ── Harakat: yordamchilar ──────────────────────────────────────────────────
/**
 * FAOL SLAYD KONTEKSTI. Slaydlar HAMMASI DOM'da turadi (almashinuv sakramasin
 * uchun), shuning uchun "hozir men ekrandaman" ma'lumoti kontekst orqali beriladi.
 * Provider qiymati o'zgarganda FAQAT uni o'qiydigan mitti tugunlar (raqam
 * hisoblagichlari) qayta chiziladi — butun slayd emas.
 */
const SlideActiveCtx = createContext<boolean>(true);

/**
 * `kRise` pog'onasi uchun `--k-i`. TypeScript `CSSProperties` maxsus CSS
 * o'zgaruvchilarini bilmaydi — shuning uchun aniq `as` bilan uzatiladi.
 */
function riseStyle(step: number, extra?: CSSProperties): CSSProperties {
  return { ...extra, '--k-i': Math.max(0, Math.min(RISE_MAX_STEP, step)) } as CSSProperties;
}

/** Ixtiyoriy CSS o'zgaruvchilarini `style` ga berish. */
function cssVars(vars: Record<string, string | number>, extra?: CSSProperties): CSSProperties {
  return { ...extra, ...vars } as CSSProperties;
}

/**
 * Sinf nomi uchun shrift koeffitsiyenti.
 *
 * Nomlar juda har xil uzunlikda: "7-A" (3 belgi) dan "1-POLARIS" (9) gacha.
 * Band kengligi esa hamma kartada BIR XIL — grid teng ustunlarga bo'linadi.
 * Qat'iy shrift bilan natija notekis chiqardi: qisqa nom keng bandda yo'qolib
 * ketardi, uzuni esa chetga tiqilib, kichikroq ekranda `text-overflow` bilan
 * QIRQILARDI ("1-POLARIS" -> "1-POLAR...").
 *
 * Chegaradan uzun nomlar proporsional kichrayadi va hech qachon qirqilmaydi.
 * Chegaragacha bo'lganlari BIR XIL o'lchamda qoladi: aks holda har karta o'z
 * o'lchamida bo'lib, qator "sakragan" ko'rinardi.
 */
const NOM_CHEGARA = 6;
function nomKoef(nom: string): number {
  const n = [...String(nom ?? '')].length;
  if (n <= NOM_CHEGARA) return 1;
  // 0.62 - pastki chegara: undan kichigi TV masofasidan o'qilmay qoladi.
  return Math.max(0.62, NOM_CHEGARA / n);
}

/** Foydalanuvchi harakatni kamaytirishni so'raganmi (TV odatda so'ramaydi). */
function prefersReducedMotion(): boolean {
  try {
    return typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * BALL SANOG'I (count-up) — 0 dan yakuniy qiymatgacha, 900ms, easeOutCubic
 * (t=0.5 da 87.5% — "avtomat" emas, "o'lchov asboblari" hissi).
 *
 * QAT'IY QOIDA: sanoq FAQAT slayd KIRISHIDA ishlaydi. Har 60 soniyada ma'lumot
 * yangilanganda raqam JIMGINA almashadi — aks holda ekran har daqiqada
 * o'z-o'zidan "sakraydi".
 * `prefers-reduced-motion` yoki nishon 0 bo'lsa — darhol yakuniy qiymat.
 */
function useCountUp(target: number): number {
  const on = useContext(SlideActiveCtx);
  const [shown, setShown] = useState(() => (on && target > 0 && !prefersReducedMotion() ? 0 : target));
  /** Joriy nishon — sanoq `on` o'zgarganda boshlanadi, `target` o'zgarganda emas. */
  const targetRef = useRef(target);
  const runningRef = useRef(false);

  useEffect(() => {
    targetRef.current = target;
    // Sanoq ketmayotgan bo'lsa yangi qiymat animatsiyasiz o'rnatiladi.
    if (!runningRef.current) setShown(target);
  }, [target]);

  useEffect(() => {
    const to = targetRef.current;
    // Nofaol slaydda hech narsa hisoblanmaydi (24/7 ekranda rAF bekorga ishlamasin).
    if (!on || to <= 0 || prefersReducedMotion()) {
      runningRef.current = false;
      setShown(to);
      return;
    }
    runningRef.current = true;
    setShown(0);
    let raf = 0;
    let t0 = 0;
    const step = (ts: number) => {
      if (!t0) t0 = ts;
      const t = Math.min(1, (ts - t0) / COUNT_MS);
      setShown(Math.round(to * (1 - (1 - t) ** 3)));
      if (t < 1) raf = window.requestAnimationFrame(step);
      else runningRef.current = false;
    };
    // Kechikish: kirish animatsiyasi tugagach boshlansin.
    const timer = window.setTimeout(() => { raf = window.requestAnimationFrame(step); }, COUNT_DELAY_MS);
    return () => {
      runningRef.current = false;
      window.clearTimeout(timer);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [on]);

  return shown;
}

/**
 * Sanab chiqiladigan raqam ALOHIDA komponent: rAF har kadrda faqat SHU tugunni
 * qayta chizadi, butun slaydni emas.
 */
function KCount({ value }: { value: number }) {
  const shown = useCountUp(value);
  return <>{shown}</>;
}

/**
 * QAHRAMON KARTA — kirish (`kRise`) va nafas (`kBreath`) NAVBAT bilan.
 *
 * NEGA: CSS da ikkala animatsiyani bitta elementga bersak, ular AYNI `transform`
 * xossasini bo'lishadi va Chrome IKKALASINI HAM kompozitsiyadan chiqaradi —
 * ya'ni har kadrda style recalc + main-thread ish. O'lchandi (CDP
 * `Performance.getMetrics`, 30 s): 60 style-recalc/s, 10.3 ms/s RecalcStyle,
 * CPU 2.5% — 24/7 ishlaydigan TV uchun qimmat.
 *
 * YECHIM: bir vaqtning o'zida elementda FAQAT BITTA transform animatsiyasi
 * bo'ladi. Slayd yoqilganda `k-rise`, kirish tugagach (`RISE_MS`) uning o'rniga
 * `is-breathing`. `kRise` ning yakuniy holati (`transform: none`) elementning
 * asosiy holatiga teng, shuning uchun almashuv ko'zga ko'rinmaydi.
 *
 * Bu ALOHIDA komponent: shunda holat o'zgarganda butun slayd emas, faqat shu
 * o'ram qayta chiziladi (`children` havolasi o'zgarmaydi → React uni o'tkazib
 * yuboradi) — `KCount` bilan bir xil tamoyil.
 */
function KHero({ className, style, children }: {
  className: string; style?: CSSProperties; children: ReactNode;
}) {
  const on = useContext(SlideActiveCtx);
  const [breathing, setBreathing] = useState(false);

  useEffect(() => {
    if (!on) { setBreathing(false); return; }
    const t = window.setTimeout(() => setBreathing(true), RISE_MS);
    return () => window.clearTimeout(t);
  }, [on]);

  return (
    <div className={`${className} ${breathing ? 'is-breathing' : 'k-rise'}`} style={style}>
      {children}
    </div>
  );
}

// ── Avatar ─────────────────────────────────────────────────────────────────
/**
 * Foto BO'LMASA yoki YUKLANMASA — bosh harflar (initsial). Qahramon kartada
 * avatar 1080p da ~250px, 4K da ~500px: shu o'lchamdagi bo'sh doira ekranda
 * "buzuq rasm" bo'lib turadi, ismning bosh harflari esa MA'LUMOT beradi.
 *
 * `onLoad` ham tekshiriladi: server 200 bilan bo'sh/xato javob qaytarsa ba'zi
 * brauzerlar `error` hodisasini bermay, rasmni 0x0 o'lchamda "yuklangan" deb
 * hisoblaydi — o'shanda ham zaxiraga o'tamiz.
 */
function KAvatar({ name, photo, kioskKey }: { name: string; photo: string | null; kioskKey: string }) {
  const src = kioskFileUrl(photo, kioskKey);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);

  if (!src || failed) return <div className="k-av k-av-ini">{initials(name)}</div>;
  return (
    <img
      className="k-av"
      src={src}
      alt=""
      decoding="async"
      onError={() => setFailed(true)}
      onLoad={(e) => {
        // MUHIM: `currentTarget` setState UPDATER ichida emas, tashqarida o'qiladi
        const empty = !e.currentTarget.naturalWidth;
        if (empty) setFailed(true);
      }}
    />
  );
}

/**
 * Avatar + burchakdagi NISHON. Geometriya podium medalining aynan o'zi
 * (`.k-medal`: avatarning 0.4 qismi, oq chegara). Bir kartada BIRDAN ORTIQ nishon
 * bo'lmaydi — chaqiruvchi ustuvorlikni o'zi hal qiladi (kubok > tort > yulduz).
 */
function KAvatarBadge({ name, photo, kioskKey, badge }: {
  name: string; photo: string | null; kioskKey: string; badge?: TablerIcon;
}) {
  const Badge = badge;
  return (
    <div className="k-avw">
      <KAvatar name={name} photo={photo} kioskKey={kioskKey} />
      {Badge ? (
        <span className="k-medal k-medal-ico" aria-hidden="true"><Badge stroke={2.4} /></span>
      ) : null}
    </div>
  );
}

/**
 * "Ball 5 yo'nalish bo'yicha" izohi. TOP-10 slaydidagi BO'SH KATAKNI dekor bilan
 * emas, MA'LUMOT bilan to'ldiradi ("nega u birinchi?" savoliga javob) va "Hafta
 * yulduzi" kartasining yonida o'quvchining kuchli tomonini ko'rsatadi.
 */
function KLegend({ row, values, step }: {
  /** Bitta QATOR balandligiga sig'adigan ixcham variant (TOP-10 dagi bo'sh katak). */
  row?: boolean;
  /** Yo'nalish ballari (0..100). Berilmasa — faqat yorliqlar chiziladi. */
  values?: Record<string, number | null>;
  /** `kRise` pog'onasidagi o'rni. */
  step: number;
}) {
  const items = values
    ? BALL_PARTS.filter((p) => values[p.key] != null)
    : BALL_PARTS;
  if (!items.length) return null;
  /**
   * Umumiy karta anatomiyasi: YUMSHOQ sarlavha bandi (bu izoh — ikkilamchi
   * kontekst, o'quvchi kartalari bilan e'tibor talashmaydi) + tana.
   * `is-strip` — `is-row` EMAS: oxirgisi `.k-card.is-row` (gorizontal karta)
   * bilan to'qnashardi.
   */
  return (
    <div className={`k-card k-legend k-rise${row ? ' is-strip' : ''}`} style={riseStyle(step)}>
      <div className="k-card-head is-soft">Ball 5 yo'nalish bo'yicha</div>
      <div className="k-card-body k-legend-items">
        {items.map((p) => {
          /**
           * Yo'nalish ulushi — kioskning boshqa joylaridagi AYNAN o'sha nozik
           * yo'lakcha (`.k-bar.is-slim`). Shkala 0..100: raqamni o'qimasdan
           * ham "qaysi yo'nalish kuchli" ko'rinadi. Yorliqsiz variantda
           * (TOP-10 dagi bo'sh katak) qiymat yo'q — yo'lakcha ham chizilmaydi.
           */
          const v = values ? Number(values[p.key]) || 0 : 0;
          return (
            <div key={p.key} className="k-legend-item">
              <KIco icon={p.icon} stroke={2.4} />
              <span className="k-legend-label">{p.label}</span>
              {values ? (
                <>
                  <span
                    className="k-bar is-slim k-legend-bar"
                    style={cssVars({ '--k-p': Math.max(0, Math.min(1, v / 100)) })}
                  >
                    <span className="k-bar-fill" />
                  </span>
                  <span className="k-legend-val">{values[p.key]}</span>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Ekran sarlavhasi ───────────────────────────────────────────────────────
/**
 * Yuklanish paytida ham CHIQADI: bo'sh ekran TV o'chgan/buzuqdek ko'rinadi.
 * Ma'lumot kelmaguncha maktab nomi o'rniga umumiy sarlavha turadi, soat esa
 * ma'lumotga bog'liq emas — darhol ishlaydi.
 */
function KioskHead({ school, clock, offline }: { school?: string; clock: TzParts; offline?: boolean }) {
  /**
   * Tagline HOLATNI aytadi. Ilgari aloqa uzilganda ham "MA'LUMOT YUKLANMOQDA"
   * turaverardi — bu yolg'on edi (hech narsa yuklanmayotgan edi).
   */
  const tagline = offline
    ? "Aloqa yo'q"
    : school ? 'Maktab axborot ekrani' : "Ma'lumot yuklanmoqda";
  return (
    <div className="k-head">
      <div className="k-brand">
        <div className="k-logo"><IconSchool stroke={2.2} /></div>
        <div className="k-brand-txt">
          <div className="k-school">{school || 'Maktab axborot ekrani'}</div>
          <div className={`k-tagline${offline ? ' is-off' : ''}`}>{tagline}</div>
        </div>
      </div>
      <div className="k-clock">
        <div className="k-time">
          {pad2(clock.h)}<span className="k-colon">:</span>{pad2(clock.m)}
          <span className="k-sec">{pad2(clock.s)}</span>
        </div>
        <div className="k-date">{clock.day}-{MONTHS[clock.month]}, {DAYS[clock.dow]}</div>
      </div>
    </div>
  );
}

// ── Slayd sarlavhasi ───────────────────────────────────────────────────────
/**
 * Sarlavha uch qismdan: ko'k tayoqcha (mavjud `::before`) → slayd IKONKASI →
 * matn. Ikonka tufayli bola 6 metrdan sarlavhani o'qimasdan ham qaysi slayd
 * ekanini biladi. Matn alohida `span` da: `text-overflow` flex konteynerning
 * anonim matn bo'lagida ishlamaydi.
 */
function SlideHead({ title, meta, kind }: { title: string; meta?: string; kind: KSlideKind }) {
  return (
    <div className="k-sh">
      <div className="k-sh-title">
        <KIco icon={SLIDE_ICON[kind]} stroke={2.4} />
        <span className="k-sh-txt">{title}</span>
      </div>
      {meta ? <div className="k-sh-meta">{meta}</div> : null}
    </div>
  );
}

// ── Reyting TOP-10 ─────────────────────────────────────────────────────────
function TopSlide({ items, periodDays, kioskKey }: { items: RankItem[]; periodDays: number; kioskKey: string }) {
  const podium = items.slice(0, 3);
  const rest = items.slice(3);
  /**
   * Podium: 2 — 1 — 3 (birinchi o'rin markazda), lekin KIRISH tartibi ma'noli —
   * g'olib birinchi "qo'nadi". Shuning uchun `step` ekrandagi joyga emas,
   * O'RINGA bog'langan (podium massividagi indeks).
   */
  const ordered = [1, 0, 2]
    .map((i) => (podium[i] ? { r: podium[i], step: i } : null))
    .filter(Boolean) as { r: RankItem; step: number }[];
  /**
   * Toq sonli qator qolganda (7 ta — TOP-10 ning odatiy holati) o'ng ustun
   * pastida bo'sh katak qoladi. `grid-auto-flow: column` da 8-element o'sha
   * katakni tabiiy egallaydi.
   */
  const withLegend = rest.length > 0 && rest.length % 2 === 1;
  const cells = rest.length + (withLegend ? 1 : 0);
  const rows = Math.max(1, Math.ceil(cells / 2));
  /**
   * BALL ULUSHINING MAXRAJI — slaydda ko'rinib turgan eng yuqori ball. Har bir
   * kartaning meta qatoridagi nozik yo'lakcha shunga nisbatan to'ladi: "u
   * qanchalik oldinda?" savoliga raqamni o'qimasdan javob beradi.
   * `Math.max(1, ...)` — barcha ballar 0 bo'lsa nolga bo'linish bo'lmasin.
   */
  const scale = Math.max(1, ...items.map((r) => roundBall(r.ball)));
  const share = (ball: number) => Math.max(0, Math.min(1, roundBall(ball) / scale));

  return (
    <>
      <SlideHead
        kind="top"
        title="O'quvchilar reytingi — TOP 10"
        meta={periodDays > 0 ? `So'nggi ${periodDays} kun` : undefined}
      />
      <div className="k-sbody">
        <div className="k-podium">
          {ordered.map(({ r, step }) => {
            const top = r.rank === 1;
            /**
             * UMUMIY KARTA ANATOMIYASI (kiosk.css "UMUMIY KARTA TIZIMI"):
             *  1) sarlavha bandi — O'RIN so'z bilan ("1-o'rin"). 1-o'rin
             *     to'ldirilgan brend, 2-3 o'rin YUMSHOQ band — bu ilovadagi
             *     `rankBoxFirst` / `rankBoxTop` pog'onasining aynan o'zi;
             *  2) tana — yuz, ISM (eng yirik), BALL (yagona asosiy raqam);
             *  3) meta — sinf yorlig'i + ball ulushi yo'lakchasi.
             * O'rin raqamli medal AVATARDAN OLIB TASHLANDI: uni sarlavha bandi
             * aytadi, kartada esa bitta asosiy raqam (ball) qolishi kerak.
             */
            const inner = (
              <>
                <div className={`k-card-head${top ? '' : ' is-soft'}`}>{r.rank}-o'rin</div>
                <div className="k-card-body k-pod-body">
                  <div className="k-pod-av">
                    {/* Gulchambar — avatar ORTIDA (z-index 0), statik SVG */}
                    {top ? <KLaurel /> : null}
                    <KAvatar name={r.fullName} photo={r.photo} kioskKey={kioskKey} />
                  </div>
                  <div className="k-pod-name">{r.fullName}</div>
                  <div className={`k-num k-pod-ball ${ballTone(r.ball)}`}>
                    <KCount value={roundBall(r.ball)} />
                    <span className="k-num-u">ball</span>
                  </div>
                  <div
                    className="k-card-meta is-wide k-pod-meta"
                    style={cssVars({ '--k-p': share(r.ball) })}
                  >
                    {r.className ? <span className="k-chip">{r.className}</span> : null}
                    <span className="k-bar is-slim">
                      <span className={`k-bar-fill${top ? ' is-top' : ''}`} />
                    </span>
                  </div>
                </div>
              </>
            );
            // Faqat 1-o'rin "nafas oladi" — shuning uchun faqat u `KHero` (qarang: KHero izohi)
            return top ? (
              <KHero key={r.rank} className="k-card is-accent k-pod k-pod-1" style={riseStyle(step)}>
                {inner}
              </KHero>
            ) : (
              <div
                key={r.rank}
                className={`k-card k-pod k-pod-${r.rank} k-rise`}
                style={riseStyle(step)}
              >
                {inner}
              </div>
            );
          })}
        </div>

        {rest.length > 0 && (
          <div className="k-rest" style={{ gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }}>
            {/*
              4-10 o'rinlar — AYNAN o'sha karta, faqat YOTIQ: sarlavha bandi chap
              chetga ko'chadi (`.k-card-head.is-side`) va o'rinni ushlab turadi.
            */}
            {rest.map((r, i) => (
              <div
                key={r.rank}
                className="k-card is-row k-rest-row k-rise"
                style={riseStyle(3 + i, cssVars({ '--k-p': share(r.ball) }))}
              >
                <span className="k-card-head is-soft is-side k-rest-rank">{r.rank}</span>
                <div className="k-card-body is-row k-rest-body">
                  <KAvatar name={r.fullName} photo={r.photo} kioskKey={kioskKey} />
                  <span className="k-rest-name">{r.fullName}</span>
                  {r.className ? <span className="k-chip">{r.className}</span> : null}
                  <span className="k-bar is-slim k-rest-bar">
                    <span className="k-bar-fill" />
                  </span>
                  <span className={`k-num k-rest-ball ${ballTone(r.ball)}`}>{roundBall(r.ball)}</span>
                </div>
              </div>
            ))}
            {withLegend ? <KLegend row step={3 + rest.length} /> : null}
          </div>
        )}
      </div>
    </>
  );
}

// ── Bugungi tug'ilgan kunlar ───────────────────────────────────────────────
/**
 * ENG KUCHLI to'xtatuvchi: bola bugun o'z sinfidan kim tug'ilganini bilishni
 * xohlaydi, tug'ilgan kun egasi esa do'stlarini ataylab ekran oldiga olib keladi.
 * Ma'lumot bo'lmasa slayd UMUMAN yaratilmaydi — "bugun tug'ilgan kun yo'q" degan
 * 12 soniyalik o'lik efir chizilmaydi.
 */
function BirthdaySlide({ items, page, pages, dateLabel, kioskKey }: {
  items: KioskBirthday[]; page: number; pages: number; dateLabel: string; kioskKey: string;
}) {
  /**
   * Maket SONGA qarab: bitta odam butun ekranni oladi (qahramon), 2-3 tasi bir
   * qatorda, 4 tadan ko'pi panjarada. Sahifalangan holatda maket doim
   * panjara — aks holda oxirgi sahifa boshqacha ko'rinib "sakrardi".
   */
  const variant = pages > 1 || items.length > 3 ? 'is-grid' : items.length === 1 ? 'is-hero' : 'is-row';
  /**
   * Ustun soni QAT'IY 4 emas, `gridCols()` bo'yicha: 6 kishi 4+2 emas, 3+3
   * bo'lib chiqadi (oxirgi qator yarim bo'sh qolmaydi). Sinf yetakchilari
   * slaydi bilan bir xil qoida — bitta maket mantig'i, ikkita slaydda.
   */
  const cols = variant === 'is-grid' ? gridCols(items.length) : 0;
  const meta = pages > 1 ? `${dateLabel} · ${page} / ${pages}` : dateLabel;
  /** Sarlavha bandining rang kodi — sinf yetakchilari bilan AYNAN bir qoida. */
  const toneCols = cols || items.length;
  return (
    <>
      <SlideHead kind="birthdays" title="Bugun tug'ilgan kun egalari" meta={meta} />
      <div className="k-sbody">
        <div
          className={`k-bds ${variant}`}
          style={cols ? { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` } : undefined}
        >
          {items.map((b, i) => {
            const tone = leaderTone(i, toneCols);
            /**
             * SINF YETAKCHISI KARTASINING AYNAN ANATOMIYASI:
             *  1) sarlavha bandi — SINF (o'sha `COL_FILL`/`COL_RAIL` rang kodi,
             *     ya'ni "sinf" tushunchasi uch slaydda bitta ko'rinishga ega);
             *  2) tana — yuz + tort nishoni, ISM (eng yirik element);
             *  3) meta — YOSH yirik raqam bilan + qisqa tabrik satri.
             * Sinf nomi yo'q bo'lsa band baribir qoladi (kartaning tepasi bo'sh
             * ko'rinmasligi uchun) va kunning o'zini aytadi.
             */
            const inner = (
              <>
                <div
                  className="k-card-head"
                  style={cssVars({ '--k-rail': COL_RAIL[tone] }, { background: COL_FILL[tone] })}
                >
                  {b.className || "Tug'ilgan kun"}
                </div>
                <div className="k-card-body k-bd-body">
                  <KAvatarBadge name={b.fullName} photo={b.photo} kioskKey={kioskKey} badge={IconCake} />
                  <div className="k-bd-name">{b.fullName}</div>
                  <div className="k-bd-foot">
                    {b.turns != null ? (
                      <div className="k-num k-bd-age">
                        {b.turns}<span className="k-num-u">yosh</span>
                      </div>
                    ) : null}
                    <div className="k-card-meta k-bd-hi">Tug'ilgan kuningiz bilan!</div>
                  </div>
                </div>
              </>
            );
            const key = `${b.fullName}-${b.className ?? ''}`;
            // Nafas faqat QAHRAMON kartada (bitta kishi) — qarang: KHero izohi
            return variant === 'is-hero' ? (
              <KHero key={key} className="k-card is-accent k-bd" style={riseStyle(i)}>{inner}</KHero>
            ) : (
              <div key={key} className="k-card k-bd k-rise" style={riseStyle(i)}>{inner}</div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ── Eng faol sinflar ───────────────────────────────────────────────────────
/**
 * "Mening sinfim ekranda!" — jamoaviy g'urur bitta o'quvchining reytingidan
 * kuchliroq to'xtatadi. Diagramma `transform: scaleX` bilan o'sadi; `width`
 * HECH QACHON animatsiya qilinmaydi (u layout hisobini qo'zg'atadi).
 */
function ClassActivitySlide({ items, periodDays }: { items: KioskClassActivity[]; periodDays: number }) {
  /**
   * DIAGRAMMA MAXRAJI. Ilgari qat'iy 100 edi, lekin reyting bali 100 dan oshishi
   * MUMKIN (skrinshotda 184 ball uchradi) — u holda `min(1, ...)` barcha
   * ustunlarni to'la ko'rsatib, diagrammani MA'NOSIZ qilardi (hamma bir xil).
   * Endi maxraj — 100 va eng katta qiymatdan kattarog'i: 100 lik shkala
   * saqlanadi (ballar past bo'lsa ustunlar haqiqatan past ko'rinadi), oshib
   * ketgan holatda esa nisbatlar tiklanadi.
   */
  const scale = Math.max(100, ...items.map((c) => Number(c.avg) || 0));
  return (
    <>
      <SlideHead
        kind="classActivity"
        title="Eng faol sinflar"
        meta={periodDays > 0 ? `So'nggi ${periodDays} kun` : undefined}
      />
      <div className="k-sbody">
        <div className="k-act">
          {items.map((c, i) => {
            /**
             * UMUMIY KARTA ANATOMIYASI, YOTIQ VARIANTI (`.k-card.is-row`) —
             * TOP-10 ning 4-10 qatorlari bilan bitta til:
             *  1) sarlavha bandi CHAP CHETDA (`.k-card-head.is-side`) — SINF
             *     nomi, o'sha `COL_FILL`/`COL_RAIL` rang kodi bilan (sinf
             *     tushunchasi to'rt slaydda bitta ko'rinishga ega);
             *  2) tana — diagramma yo'lakchasi + BALL (yagona asosiy raqam);
             *  3) meta — o'quvchilar soni tabletkasi.
             * Bandning kengligi QAT'IY (`.k-act-cls` da) — shunda barcha
             * yo'lakchalar bir vertikalda boshlanadi va diagramma o'qiladi.
             */
            const top = i === 0;
            const p = Math.max(0, Math.min(1, (Number(c.avg) || 0) / scale));
            return (
              <div
                key={c.className}
                className={`k-card is-row k-act-row k-rise${top ? ' is-accent' : ''}`}
                style={riseStyle(i, cssVars({ '--k-p': p }))}
              >
                <div
                  className="k-card-head is-side k-act-cls"
                  style={cssVars(
                    { '--k-rail': COL_RAIL[i % 3], '--k-head-k': nomKoef(c.className) },
                    { background: COL_FILL[i % 3] }
                  )}
                  title={c.className}
                >
                  {c.className}
                </div>
                <div className="k-card-body is-row k-act-body">
                  <span className="k-bar">
                    <span className={`k-bar-fill${top ? ' is-top' : ''}`} />
                  </span>
                  <span className={`k-num k-act-ball ${ballTone(Number(c.avg) || 0)}`}>
                    {c.avg}<span className="k-num-u">ball</span>
                  </span>
                  <span className="k-card-meta is-pill k-act-cnt">{c.count} o'quvchi</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ── Hafta yulduzi ──────────────────────────────────────────────────────────
/**
 * 30 kunlik reyting sekin o'zgaradi — bir hafta ko'rgan bola bir xil yuzlarni
 * ko'raveradi. 7 kunlik oyna "bu hafta kim ko'tarildi" ni ko'rsatadi.
 * Maket "tug'ilgan kun" ning bir kishilik variantiga TENG (yangi maket ixtiro
 * qilinmaydi).
 */
function WeekStarSlide({ star, kioskKey }: { star: KioskWeekStar; kioskKey: string }) {
  const parts = BALL_PARTS.filter((p) => star.breakdown[p.key] != null);
  const hasParts = parts.length > 0;
  /**
   * ENG KUCHLI YO'NALISH — meta qatorining MA'LUMOTI: "nega u yulduz?" savoliga
   * bir qatorda javob beradi (yonidagi izoh kartasi esa beshalasini yoyib
   * ko'rsatadi). Ma'lumot yo'q bo'lsa meta davr nomini aytadi — band bo'sh
   * qolmaydi.
   */
  const best = hasParts
    ? parts.reduce((a, p) => ((star.breakdown[p.key] as number) > (star.breakdown[a.key] as number) ? p : a))
    : null;
  const BestIco = best?.icon;
  return (
    <>
      <SlideHead kind="weekStar" title="Hafta yulduzi" meta="So'nggi 7 kun" />
      <div className="k-sbody">
        <div className={`k-star${hasParts ? '' : ' is-solo'}`}>
          {/*
            QAHRAMON KARTA — umumiy karta anatomiyasi (kirish va nafas navbat
            bilan, qarang: KHero izohi):
             1) sarlavha bandi — SINF (yetakchi/tug'ilgan kun kartalari bilan
                bitta rang kodi va bitta ma'no);
             2) tana — yuz + yulduz nishoni, ISM (eng yirik), BALL;
             3) meta — eng kuchli yo'nalish.
          */}
          <KHero className="k-card is-accent k-star-hero" style={riseStyle(0)}>
            <div className="k-card-head k-star-head">{star.className || 'Hafta yulduzi'}</div>
            <div className="k-card-body is-center k-star-body">
              <KAvatarBadge name={star.fullName} photo={star.photo} kioskKey={kioskKey} badge={IconStar} />
              <div className="k-star-name">{star.fullName}</div>
              {/* Katta raqam umumiy `.k-num` shaklidan (ohang = QIYMAT) */}
              <div className={`k-num k-star-ball ${ballTone(star.ball)}`}>
                <KCount value={roundBall(star.ball)} />
                <span className="k-num-u">ball</span>
              </div>
              <div className="k-card-meta is-pill is-accent k-star-meta">
                {BestIco ? <BestIco stroke={2.4} aria-hidden="true" /> : null}
                {best ? `Eng kuchli: ${best.label}` : "So'nggi 7 kun yulduzi"}
              </div>
            </div>
          </KHero>
          {hasParts ? <KLegend values={star.breakdown} step={1} /> : null}
        </div>
      </div>
    </>
  );
}

// ── Sinf yetakchilari ──────────────────────────────────────────────────────
/**
 * USTUN SONI (sinf yetakchilari VA tug'ilgan kunlar panjarasi uchun umumiy).
 * Ilgari `Math.ceil(Math.sqrt(n))` ishlatilardi va 10 ta sinf uchun
 * 4 berardi (4+4+2 — oxirgi qator yarim bo'sh). Yangi qoida: `[5, 4, 3]` dan
 * BIRINCHI "toza" bo'linadigani olinadi (oxirgi qator to'la yoki bittagina
 * katak kam bo'lsa). 10→5 (5+5), 11→4 (4+4+3), 7→4 (4+3), 12→4 (4+4+4),
 * 6→3 (3+3).
 */
function gridCols(n: number): number {
  if (n <= 0) return 1;
  const c = [5, 4, 3].find((x) => n % x === 0 || n % x >= x - 1) ?? 4;
  return Math.max(1, Math.min(c, n));
}

/**
 * Karta SARLAVHASINING rang kodi (`COL_FILL`/`COL_RAIL` indeksi). Rang SINFGA
 * biriktirilgan va vazifasi bitta — yonma-yon turgan kartalarni ajratish.
 *
 * Oddiy `i % 3` panjarada bitta kamchilikka ega: ustun soni 3 ga BO'LINSA
 * (ya'ni 3 ustun) qatorlar ustma-ust aynan bir xil ton bilan tushadi. Shunday
 * holatda har qator bir pog'ona suriladi — shunda vertikal qo'shnilar ham
 * farqlanadi. 4 va 5 ustunda surish KERAK EMAS va HATTO ZARARLI: 5 ustunda
 * surilgan qator ustidagi qator bilan aynan bir xil tonga tushib qolardi.
 */
function leaderTone(i: number, cols: number): number {
  const c = Math.max(1, cols);
  return (i + (c % 3 === 0 ? Math.floor(i / c) : 0)) % 3;
}

function LeadersSlide({ items, page, pages, scale, kioskKey }: {
  items: ClassLeader[]; page: number; pages: number; scale: number; kioskKey: string;
}) {
  const cols = gridCols(items.length);
  const rows = Math.ceil(items.length / cols);
  /**
   * ZICHLIKNING UCH POG'ONASI — karta balandligi qator soniga teskari
   * proporsional, tipografiya esa unga ergashadi (dars jadvalidagi `is-dense`
   * bilan bir xil tamoyil). Hech qanday MA'LUMOT olib tashlanmaydi:
   *  • 1 qator (≤5 sinf) — `is-wide`: sahna baland, bo'g'inlar yirikroq;
   *  • 2 qator (odatiy 6-10 sinf) — asosiy o'lchamlar;
   *  • 3+ qator (11-12 sinf) — `is-dense`: hammasi bir pog'ona kichik.
   */
  const size = rows <= 1 ? ' is-wide' : rows >= 3 ? ' is-dense' : '';
  return (
    <>
      <SlideHead kind="leaders" title="Sinf yetakchilari" meta={pages > 1 ? `${page} / ${pages}` : undefined} />
      <div className="k-sbody">
        <div
          className={`k-leaders${size}`}
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {items.map((l, i) => {
            /**
             * Maktab bo'yicha 1-o'rin egasi ekranda BITTA karta oladi. Urg'u
             * BITTA joyga to'plangan: brend chegara + pastki qatordagi
             * to'ldirilgan "Maktab yetakchisi" yorlig'i (kubok ikonkasi shu
             * yerda — u endi yolg'iz emas, yozuv bilan birga turadi).
             */
            const isTop = l.item.rank === 1;
            const ball = roundBall(l.item.ball);
            /** Maktabdagi eng yuqori balga NISBAT (0..1) — yo'lakcha uzunligi. */
            const p = scale > 0 ? Math.max(0, Math.min(1, ball / scale)) : 0;
            /** Ma'lumot buzuq bo'lsa (o'rin yo'q) qator umuman chizilmaydi. */
            const rank = Number(l.item.rank) || 0;
            const tone = leaderTone(i, cols);
            return (
              <div
                key={l.className}
                className={`k-card k-leader k-rise${isTop ? ' is-accent' : ''}`}
                style={riseStyle(i, cssVars({ '--k-p': p }))}
              >
                {/* To'ldirilgan sarlavha — jadval ustuni sarlavhasining aynan naqshi */}
                <div
                  className="k-card-head"
                  style={cssVars(
                    { '--k-rail': COL_RAIL[tone], '--k-head-k': nomKoef(l.className) },
                    { background: COL_FILL[tone] }
                  )}
                  title={l.className}
                >
                  {l.className}
                </div>
                <div className="k-card-body">
                  <KAvatar name={l.item.fullName} photo={l.item.photo} kioskKey={kioskKey} />
                  {/* Ichki `span` — qator cheklovi; tashqi quti matnni markazlaydi (kiosk.css) */}
                  <div className="k-leader-name"><span>{l.item.fullName}</span></div>
                  <div className="k-leader-score">
                    <div className={`k-num k-leader-ball ${ballTone(l.item.ball)}`}>
                      {ball}<span className="k-num-u">ball</span>
                    </div>
                    {/* "Eng faol sinflar" diagrammasining aynan yo'lakchasi, faqat nozik */}
                    <span className="k-bar is-slim">
                      <span className={`k-bar-fill${isTop ? ' is-top' : ''}`} />
                    </span>
                  </div>
                  {rank > 0 ? (
                    <div className={`k-card-meta is-pill${isTop ? ' is-accent' : ''}`}>
                      {isTop ? (
                        <>
                          <IconTrophy stroke={2.4} aria-hidden="true" />
                          Maktab yetakchisi
                        </>
                      ) : `Maktabda ${rank}-o'rin`}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ── Dars jadvali ───────────────────────────────────────────────────────────
/**
 * Ustunlar UMUMIY VAQT O'QIGA tekislanadi: `slots` — sahifadagi barcha sinflarning
 * boshlanish vaqtlari. Har ustun aynan shu qatorlarni chizadi, o'sha vaqtda darsi
 * yo'q sinfda katak BO'SH qoladi. Shu sabab bir xil vaqtdagi darslar bir balandlikda
 * turadi va "Hozir" butun ekran bo'ylab gorizontal chiziq hosil qiladi.
 * Qatorlar CSS grid bilan teng bo'linadi (`gridTemplateRows`), ustunlar bir xil
 * balandlikda bo'lgani uchun qatorlar avtomatik mos tushadi.
 *
 * `slots` — shu slaydga TEGISHLI vaqt oynasi (kunning hammasi emas): o'q uzun
 * bo'lsa slaydlarga bo'linadi (qarang: slaydlarni tuzish, `MAX_SLOTS`). Shuning
 * uchun bo'lingan holatda sarlavhada oyna chegarasi ham yoziladi.
 */
function ScheduleSlide({ dow, isToday, cols, slots, timePages, hiddenSlots, page, pages, nowMin }: {
  dow: number; isToday: boolean; cols: ScheduleCol[]; slots: string[]; timePages: number;
  hiddenSlots: number; page: number; pages: number; nowMin: number;
}) {
  const title = isToday ? 'Bugungi dars jadvali' : `${DAYS[dow] || ''} jadvali`;
  const inWindow = new Set(slots);
  /**
   * Vaqt o'qi slaydlarga bo'lingan bo'lsa, sarlavhada qaysi ORALIQ ko'rinayotgani
   * YOZILISHI SHART — aks holda tomoshabin "kunning hamma darsi shu" deb o'ylaydi.
   */
  let endLabel = '';
  let endMin = -1;
  for (const { lessons } of cols) {
    for (const l of lessons) {
      if (!inWindow.has(l.startTime)) continue;
      const e = toMinutes(l.endTime);
      if (e > endMin) { endMin = e; endLabel = l.endTime; }
    }
  }
  const metaParts: string[] = [];
  if (pages > 1) metaParts.push(`${page} / ${pages}`);
  if (timePages > 1 && slots.length) metaParts.push(`${slots[0]}–${endLabel || slots[slots.length - 1]}`);
  if (!metaParts.length) metaParts.push(`${cols.length} sinf`);
  const meta = metaParts.join(' · ');
  const rows = `repeat(${Math.max(1, slots.length)}, minmax(0, 1fr))`;
  /**
   * 7 ta va undan ko'p vaqt oralig'ida uchta qator (vaqt/fan/o'qituvchi) TV'da
   * o'qilarli o'lchamda sig'maydi — CSS `is-dense` da o'qituvchi ismini olib
   * tashlab, qolganini bir pog'ona kichraytiradi (kiosk.css ga qarang).
   */
  const dense = slots.length >= DENSE_SLOTS;
  return (
    <>
      <SlideHead kind="schedule" title={title} meta={meta} />
      <div className="k-sbody">
        <div className="k-sched" style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` }}>
          {cols.map(({ cls, lessons }, ci) => {
            // Vaqt → o'sha vaqtda boshlanadigan DARSLAR. Bitta sinfda ayni vaqtda
            // bir nechta yozuv bo'lishi mumkin (guruhlarga bo'lingan dars) —
            // ilgari faqat birinchisi olinib, qolgani JIMGINA yo'qolardi.
            const byStart = new Map<string, KioskEntry[]>();
            for (const l of lessons) {
              const arr = byStart.get(l.startTime);
              if (arr) arr.push(l);
              else byStart.set(l.startTime, [l]);
            }
            /*
             * USTUN — o'sha umumiy karta qobig'i (`.k-card`): radius, chegara,
             * soya va sarlavha bandi boshqa slaydlardagi kartalar bilan AYNAN
             * bir xil. Farq faqat mazmunda — tana o'rnida dars qatorlari
             * (ular karta EMAS, ichki ajratgichli qatorlar).
             */
            return (
              <div key={cls.id} className="k-card k-col k-rise" style={riseStyle(ci)}>
                {/* Rang kodi ustunlarni ajratadi — sinf yetakchisi/tug'ilgan kun
                    kartalaridagi band bilan bitta ma'no, bitta ko'rinish */}
                <div
                  className="k-card-head"
                  style={cssVars({ '--k-rail': COL_RAIL[ci % 3] }, { background: COL_FILL[ci % 3] })}
                >
                  {cls.name}
                </div>
                <div className={`k-lessons${dense ? ' is-dense' : ''}`} style={{ gridTemplateRows: rows }}>
                  {slots.map((slot) => {
                    const group = byStart.get(slot);
                    const l = group ? group[0] : undefined;
                    if (!l) return <div key={slot} className="k-lesson is-empty" />;
                    const extra = (group as KioskEntry[]).slice(1).map((x) => x.subjectName).filter(Boolean);
                    const s = toMinutes(l.startTime);
                    const e = toMinutes(l.endTime);
                    const now = isToday && s >= 0 && e > s && nowMin >= s && nowMin < e;
                    const past = isToday && e >= 0 && nowMin >= e;
                    return (
                      <div key={slot} className={`k-lesson${now ? ' is-now' : ''}${past ? ' is-past' : ''}`}>
                        <div className="k-l-time">
                          <span className="k-l-hours">{l.startTime}–{l.endTime}</span>
                          <span className="k-l-tags">
                            {/* Xona raqami "Hozir" yorlig'i bilan BIRGA ko'rinadi (biri ikkinchisini almashtirmaydi) */}
                            {l.room ? <span className="k-room">{l.room}</span> : null}
                            {now ? <span className="k-now-badge">Hozir</span> : null}
                          </span>
                        </div>
                        {/* Fan ikonkasi: "hozir matematika" ni 6 metrdan BIR QARASHDA beradi */}
                        <div className="k-l-subj">
                          <KIco icon={subjectIcon(l.subjectName)} stroke={dense ? 2.4 : 2} />
                          <span className="k-l-name">{l.subjectName}</span>
                        </div>
                        {/* Ayni vaqtdagi qo'shimcha darslar (guruhlar) — zich rejimda ham qoladi */}
                        {extra.length ? <div className="k-l-extra">+ {extra.join(', ')}</div> : null}
                        {/* Qator balandligi qat'iy: qo'shimcha dars bo'lsa ierarxiyaning eng
                            pastki bo'g'ini — o'qituvchi ismi — o'rnini unga bo'shatadi. */}
                        {l.teacherName && !extra.length ? <div className="k-l-teacher">{l.teacherName}</div> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {/* Bu holat faqat ma'lumot g'ayritabiiy bo'lganda (MAX_TIME_PAGES ga ham
            sig'maganda) chiqadi — lekin JIM QOLMAYDI. */}
        {hiddenSlots > 0 ? <div className="k-more">Yana {hiddenSlots} ta dars vaqti ko'rsatilmadi</div> : null}
      </div>
    </>
  );
}

// ── Banner ─────────────────────────────────────────────────────────────────
/**
 * Kiosk o'zbekcha, lekin banner faqat ru/en da yozilgan bo'lishi mumkin —
 * u holda bo'sh slayd 12 soniya turmasligi uchun ru → en ga tushamiz.
 * (pickLang uz'ni asos qilib oladi, shuning uchun uz bor bo'lsa u g'olib chiqadi.)
 */
function pickBannerText(content: BannerContent | null): BannerLang {
  const uz = pickLang(content, 'uz');
  if (uz.title || uz.body) return uz;
  const ru = pickLang(content, 'ru');
  if (ru.title || ru.body) return ru;
  const en = pickLang(content, 'en');
  if (en.title || en.body) return en;
  return uz;
}

function BannerSlide({ banner, page, pages, kioskKey }: {
  banner: KioskBanner; page: number; pages: number; kioskKey: string;
}) {
  const tpl: BannerTemplate = TEMPLATES.includes(banner.template) ? banner.template : 'GRADIENT';
  const d = mergeDesign(tpl, banner.design);
  const c = pickBannerText(banner.content);
  const th = d.light; // kiosk yorug' temada — dizaynning LIGHT varianti
  const img = kioskFileUrl(th.imageKey, kioskKey);
  const hasGrad = !!th.useGradient && !!(th.grad1 || th.grad2);
  const hasSolid = !!th.bg;
  /**
   * Oq matn FAQAT to'q yuza ustida: rasm (scrim bor) yoki rangli/gradientli fon
   * 'light' ohangda bo'lsa. Foni yo'q banner endi OQ KARTA — matn to'q bo'lishi
   * shart (qorong'i kioskda bu shart teskari edi va oq fonda oq matn chiqardi).
   */
  const lightText = !!img || ((hasGrad || hasSolid) && th.text !== 'dark');

  const plain = !img && !hasGrad && !hasSolid;
  const style: CSSProperties = {};
  if (img) style.backgroundImage = `url("${img}")`;
  else if (hasGrad) style.backgroundImage = `linear-gradient(135deg, ${th.grad1 || th.bg || '#3D8BFF'}, ${th.grad2 || th.bg || '#0A3E96'})`;
  else if (hasSolid) style.backgroundColor = th.bg;
  // plain: fon `.k-banner.is-plain` dan (oq karta + ichki chegara)

  const fg = lightText ? '#FFFFFF' : '#0F1B33';
  const dim = lightText ? 'rgba(255,255,255,0.88)' : 'rgba(15,27,51,0.68)';
  /** Oq karta ustida oq aksent (chiziq/eyebrow) ko'rinmaydi — brendga qaytamiz. */
  const whiteAccent = /^#(fff|ffffff)$/i.test((th.accent || '').trim());
  const accent = lightText
    ? th.accent || '#FFFFFF'
    : (th.accent && !whiteAccent ? th.accent : '#0068F8');
  const centered = d.align === 'center';

  /**
   * CTA. TV'ni bosib bo'lmaydi, lekin admin CTA maydoniga MA'LUMOT yozadi
   * ("Batafsil: 71-200-00-00" kabi) va `templateDefaults('HERO')` da
   * `showCta: true`. Ilgari kiosk `c.cta` ni umuman chizmasdi — admin yozgan
   * matn TV'da jimgina yo'qolardi. Tanlov: CTA ni BOSILMAYDIGAN YORLIQ sifatida
   * chizamiz (tugma emas — soya/hover yo'q, kursor o'zgarmaydi). Konstruktorni
   * o'zgartirish (showCta ni o'chirish) ma'lumotni yo'qotardi, chunki telefon
   * ilovasida o'sha banner uchun CTA kerak.
   */
  const cta = d.showCta && c.cta ? c.cta : '';
  const ctaStyle: CSSProperties = lightText
    ? { background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.6)', color: '#FFFFFF' }
    : {
      background: hexAlpha(accent, 0.12) || '#E7F0FF',
      borderColor: hexAlpha(accent, 0.4) || '#0068F8',
      color: accent,
    };

  return (
    <>
      {/* Boshqa slaydlar bilan bir xil sarlavha: usiz aylanishda sahnaning
          yuqori chegarasi sakrab turardi. */}
      <SlideHead kind="banner" title="E'lonlar" meta={pages > 1 ? `${page} / ${pages}` : undefined} />
      <div className="k-sbody">
        <div
          className={`k-banner${centered ? ' is-center' : ''}${img ? ' is-photo' : ''}${plain ? ' is-plain' : ''}`}
          style={style}
        >
          {img && d.scrim !== false ? <span className="k-banner-scrim" /> : null}
          {plain ? <span className="k-banner-rule" style={{ background: accent }} /> : null}
          {/*
            EMOJI KIOSKDA CHIZILMAYDI. Loyiha qoidasi "UI'da emoji-stiker
            taqiqlangan", va koridor TV'sida xom emoji butun ekranning
            jiddiyligini yo'q qiladi. MUHIM: `bannerDesign.ts` konstruktoriga
            TEGILMAYDI — ilova/admin panelidagi banner o'z emojisini saqlaydi,
            faqat KIOSK uni ko'rsatmaydi.
          */}
          <div className="k-banner-in">
            {c.eyebrow ? <div className="k-b-eyebrow" style={{ color: accent }}>{c.eyebrow}</div> : null}
            {c.title ? <div className="k-b-title" style={{ color: fg }}>{c.title}</div> : null}
            {c.body ? <div className="k-b-body" style={{ color: dim }}>{c.body}</div> : null}
            {cta ? <div className="k-b-cta" style={ctaStyle}>{cta}</div> : null}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Kalit kiritish ekrani ──────────────────────────────────────────────────
function SetupScreen({ value, error, onChange, onSubmit }: {
  value: string; error: string | null; onChange: (v: string) => void; onSubmit: (e: FormEvent) => void;
}) {
  return (
    <div className="k-setup">
      <form className="k-setup-card" onSubmit={onSubmit}>
        <div className="k-logo k-logo-lg"><IconSchool stroke={2.2} /></div>
        <div className="k-setup-title">Kiosk ekrani</div>
        <div className="k-setup-text">
          Ekranni ishga tushirish uchun maktab kiosk kalitini kiriting.
          Kalitni admin panelning Sozlamalar bo'limidan olasiz.
        </div>
        <input
          className="k-input"
          type="password"
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
          placeholder="Kiosk kaliti"
          autoComplete="off"
          spellCheck={false}
          aria-label="Kiosk kaliti"
        />
        {error ? <div className="k-setup-err">{error}</div> : null}
        <button className="k-btn" type="submit">Saqlash</button>
      </form>
    </div>
  );
}

// ── Xatolik to'sig'i ───────────────────────────────────────────────────────
/**
 * TV oyoqda turishi shart: render xatosi ekranni butunlay bo'sh qoldirmasin.
 * Xato bo'lsa brend uslubidagi xabar ko'rsatiladi va 60 soniyadan keyin
 * sahifa o'zi qayta yuklanadi.
 */
class KioskErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  private reloadTimer: number | null = null;

  state: { failed: boolean } = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch() {
    if (this.reloadTimer === null) {
      this.reloadTimer = window.setTimeout(() => window.location.reload(), CRASH_RELOAD_MS);
    }
  }

  componentWillUnmount() {
    if (this.reloadTimer !== null) {
      window.clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="sata-kiosk">
          <div className="k-crash">
            <div className="k-logo k-logo-lg"><IconSchool stroke={2.2} /></div>
            <div className="k-crash-title">Texnik uzilish</div>
            <div className="k-crash-text">Ekran tez orada qayta yuklanadi</div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Asosiy sahifa ──────────────────────────────────────────────────────────
export default function Kiosk() {
  return (
    <KioskErrorBoundary>
      <KioskScreenPage />
    </KioskErrorBoundary>
  );
}

function KioskScreenPage() {
  const [params, setParams] = useSearchParams();
  const [kioskKey, setKioskKey] = useState<string>(readStoredKey);
  const [keyInput, setKeyInput] = useState('');
  const [keyError, setKeyError] = useState<string | null>(null);
  /** Kalit serverda topilmadi (404) — kalit saqlanib qoladi, fonda qayta urinamiz. */
  const [keyRejected, setKeyRejected] = useState(false);
  /** Kalitni qayta yuborishda darhol so'rov qilish uchun. */
  const [retryNonce, setRetryNonce] = useState(0);
  const [data, setData] = useState<KioskScreen | null>(null);
  const [offline, setOffline] = useState(false);
  const [index, setIndex] = useState(0);
  const [isFs, setIsFs] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  /** Server va TV soati orasidagi farq (ms) — jonli soat va "Hozir" uchun. */
  const offsetRef = useRef(0);
  /** So'rov navbati: eskirgan javob yangi ma'lumot ustidan yozmasligi uchun. */
  const seqRef = useRef(0);
  const appliedSeqRef = useRef(0);
  /** Bir vaqtda faqat bitta so'rov ketsin (osilgan ulanish ustiga yangisi qalashmasin). */
  const inFlightRef = useRef(false);

  // URL kalitini saqlab, manzil satridan olib tashlaymiz (ekranda ko'rinmasin)
  useEffect(() => {
    const k = params.get('key');
    if (!k) return;
    try { localStorage.setItem(KEY_LS, k.trim()); } catch { /* localStorage o'chirilgan */ }
    setKioskKey(k.trim());
    setKeyRejected(false);
    setKeyError(null);
    setParams({}, { replace: true });
  }, [params, setParams]);

  // ── Ma'lumot: 60 soniyada bir marta ──────────────────────────────────────
  const load = useCallback(async (outerSignal: AbortSignal) => {
    if (inFlightRef.current) return; // oldingi so'rov hali tugamagan
    inFlightRef.current = true;

    const seq = ++seqRef.current;
    // AbortSignal.timeout() eski TV brauzerlarida yo'q — qo'lda taymer.
    const ctrl = new AbortController();
    let timedOut = false;
    const onOuterAbort = () => ctrl.abort();
    if (outerSignal.aborted) ctrl.abort();
    else outerSignal.addEventListener('abort', onOuterAbort);
    const timer = window.setTimeout(() => { timedOut = true; ctrl.abort(); }, FETCH_TIMEOUT_MS);
    /** Kechikib kelgan eski javob yangi ma'lumotni (va soat farqini) buzmasin. */
    const stale = () => seq <= appliedSeqRef.current;

    try {
      const res = await fetch(`${API_URL}/kiosk/screen?key=${encodeURIComponent(kioskKey)}`, { signal: ctrl.signal });
      if (res.status === 404) {
        if (stale()) return;
        appliedSeqRef.current = seq;
        // Kalitni O'CHIRMAYMIZ: maktab vaqtincha to'xtatilgan bo'lishi mumkin —
        // fonda o'sha kalit bilan urinishda davom etamiz, tiklansa o'zi ochiladi.
        setKeyRejected(true);
        setKeyError("Kiosk kaliti noto'g'ri yoki vaqtincha bekor qilingan. Qayta urinilmoqda… Yangi kalit bo'lsa, kiriting.");
        return;
      }
      if (!res.ok) {
        if (stale()) return;
        appliedSeqRef.current = seq;
        setOffline(true);
        return;
      }
      const json = (await res.json()) as KioskScreen;
      if (stale()) return;
      appliedSeqRef.current = seq;
      const t = Date.parse(json.serverTime);
      if (!Number.isNaN(t)) offsetRef.current = t - Date.now();
      setData(json);
      setOffline(false);
      setKeyRejected(false); // kalit yana ishlayapti — aylanma davom etadi
      setKeyError(null);
    } catch (err) {
      // Taymer bo'yicha uzilgan so'rov ham "aloqa yo'q" holati (oxirgi ma'lumot qoladi)
      if ((err as Error)?.name === 'AbortError' && !timedOut) return;
      if (stale()) return;
      appliedSeqRef.current = seq;
      setOffline(true);
    } finally {
      window.clearTimeout(timer);
      outerSignal.removeEventListener('abort', onOuterAbort);
      if (seq === seqRef.current) inFlightRef.current = false;
    }
  }, [kioskKey]);

  useEffect(() => {
    if (!kioskKey) return;
    const ctrl = new AbortController();
    inFlightRef.current = false; // yangi kalit/urinish — eski so'rov bekor qilindi
    void load(ctrl.signal);
    const id = window.setInterval(() => { void load(ctrl.signal); }, POLL_MS);
    return () => { window.clearInterval(id); ctrl.abort(); };
  }, [kioskKey, load, retryNonce]);

  // ── Jonli soat (server vaqtiga moslangan) ────────────────────────────────
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now() + offsetRef.current), 1000);
    return () => window.clearInterval(id);
  }, []);

  const clock = useMemo(() => tzParts(nowMs), [nowMs]);
  const nowMin = clock.h * 60 + clock.m;

  // ── Ekran o'chmasin ──────────────────────────────────────────────────────
  useEffect(() => {
    let sentinel: WakeLockSentinel | null = null;
    let disposed = false;

    const acquire = async () => {
      if (!('wakeLock' in navigator) || document.visibilityState !== 'visible') return;
      try {
        const s = await navigator.wakeLock.request('screen');
        if (disposed) { void s.release().catch(() => undefined); return; }
        sentinel = s;
      } catch { /* TV qo'llab-quvvatlamasligi mumkin */ }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && (!sentinel || sentinel.released)) void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (sentinel) void sentinel.release().catch(() => undefined);
      sentinel = null;
    };
  }, []);

  // ── To'liq ekran holati ──────────────────────────────────────────────────
  useEffect(() => {
    const sync = () => setIsFs(!!document.fullscreenElement);
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  // ── Slaydlar ─────────────────────────────────────────────────────────────
  const slides = useMemo<Slide[]>(() => {
    if (!data) return [];
    const out: Slide[] = [];

    const periodDays = data.period?.days ?? 0;

    const top = data.ranking?.top ?? [];
    if (top.length) out.push({ key: 'top', kind: 'top', items: top.slice(0, 10), periodDays });

    /**
     * BUGUNGI TUG'ILGAN KUNLAR. Bo'sh bo'lsa slayd YARATILMAYDI — "bugun
     * tug'ilgan kun yo'q" degan bo'sh holat 12 soniyalik o'lik efir bo'lardi.
     * Sana server vaqtidan olinadi (TV soati noto'g'ri bo'lsa ham to'g'ri chiqadi).
     */
    const birthdays = data.birthdays ?? [];
    if (birthdays.length) {
      const st = Date.parse(data.serverTime);
      const sp = tzParts(Number.isNaN(st) ? Date.now() : st);
      const dateLabel = `${sp.day}-${MONTHS[sp.month]}`;
      /**
       * TENG SAHIFALASH — dars jadvalidagi vaqt o'qi mantig'ining aynan o'zi:
       * avval SAHIFA SONI hisoblanadi, keyin kartalar shu sahifalarga teng
       * bo'linadi. Ilgari 12 kishi 8+4 bo'lib, ikkinchi sahifa yarim bo'sh
       * qolardi; endi 6+6.
       */
      const bdPerPage = Math.max(1, Math.ceil(
        birthdays.length / Math.ceil(birthdays.length / BIRTHDAYS_PER_SLIDE),
      ));
      const bdChunks: KioskBirthday[][] = [];
      for (let i = 0; i < birthdays.length; i += bdPerPage) {
        bdChunks.push(birthdays.slice(i, i + bdPerPage));
      }
      // Sahifa soni HAQIQIY bo'laklar bo'yicha (yaxlitlash chekka holatlarida
      // "1 / 2" degan yolg'on ko'rsatkich chiqmasin).
      bdChunks.forEach((chunk, i) => {
        out.push({
          key: `bd-${i}`,
          kind: 'birthdays',
          items: chunk,
          page: i + 1,
          pages: bdChunks.length,
          dateLabel,
        });
      });
    }

    const leaders = data.ranking?.classLeaders ?? [];
    const leaderPages = Math.ceil(leaders.length / LEADERS_PER_SLIDE);
    /**
     * YO'LAKCHA MAXRAJI — "Eng faol sinflar" dagi qoidaning aynan o'zi: 100 lik
     * shkala saqlanadi (ballar past bo'lsa yo'lakchalar haqiqatan qisqa
     * ko'rinadi), lekin ball 100 dan oshsa (uchraydi) maxraj ko'tariladi.
     * Manba — maktab bo'yicha 1-o'rin balli; TOP-10 bo'sh bo'lsa yetakchilar
     * ichidagi maksimum. HAMMA sahifada bir xil bo'lishi SHART.
     */
    const scale = Math.max(
      100,
      Number(top[0]?.ball) || 0,
      ...leaders.map((l) => Number(l.item?.ball) || 0),
    );
    for (let i = 0; i < leaders.length; i += LEADERS_PER_SLIDE) {
      out.push({
        key: `lead-${i}`,
        kind: 'leaders',
        items: leaders.slice(i, i + LEADERS_PER_SLIDE),
        page: i / LEADERS_PER_SLIDE + 1,
        pages: leaderPages,
        scale,
      });
    }

    /**
     * ENG FAOL SINFLAR. Server 2 tadan kam sinf qolganda bo'sh massiv qaytaradi
     * (bitta ustunli diagramma ma'nosiz) — front qo'shimcha tekshiruv qilmaydi.
     */
    const activity = data.classActivity ?? [];
    if (activity.length > 1) {
      out.push({ key: 'act', kind: 'classActivity', items: activity, periodDays });
    }

    const entries = data.schedule?.entries ?? [];
    const dow = pickDow(entries, data.todayDow);
    if (dow) {
      const byClass = new Map<string, KioskEntry[]>();
      for (const e of entries) {
        if (e.dayOfWeek !== dow) continue;
        const arr = byClass.get(e.classId);
        if (arr) arr.push(e);
        else byClass.set(e.classId, [e]);
      }
      const cols: ScheduleCol[] = (data.schedule?.classes ?? [])
        .filter((c) => byClass.has(c.id))
        .map((c) => ({
          cls: c,
          lessons: (byClass.get(c.id) as KioskEntry[]).slice().sort((a, b) => a.startTime.localeCompare(b.startTime)),
        }));
      /**
       * IKKI O'LCHOVLI SAHIFALASH.
       *  • USTUNLAR — har slaydda ko'pi bilan CLASSES_PER_SLIDE ta sinf;
       *  • QATORLAR — umumiy vaqt o'qi MAX_SLOTS dan uzun bo'lsa, u ham
       *    slaydlarga bo'linadi.
       * Ilgari o'q shunchaki `slice(0, MAX_LESSONS)` bilan KESILARDI. Kesish
       * UMUMIY o'qqa tegishli bo'lgani uchun, darslari kech boshlanadigan sinf
       * (ikkinchi smena) ekranga UMUMAN tushmasdi va buni hech kim sezmasdi.
       * Endi hech narsa kesilmaydi — vaqt oynasi almashib turadi.
       *
       * Qatorlar sahifalar orasida TENG taqsimlanadi (12 ta vaqt → 6+6, 8+4 emas):
       * shunda qator balandligi ham baland qoladi va zich rejim kamroq kerak
       * bo'ladi. Har oynaning vaqt oralig'i slayd sarlavhasida yoziladi.
       */
      const schedSlides: ScheduleSlideData[] = [];
      for (let i = 0; i < cols.length; i += CLASSES_PER_SLIDE) {
        const pageCols = cols.slice(i, i + CLASSES_PER_SLIDE);
        // Umumiy vaqt o'qi — shu SAHIFADAGI sinflarning barcha boshlanish vaqtlari.
        // Sahifa bo'yicha hisoblanadi: boshqa sahifadagi vaqt bu yerda bo'sh qator
        // qoldirmasin (ekran zich to'lsin).
        const slotSet = new Set<string>();
        for (const c of pageCols) for (const l of c.lessons) slotSet.add(l.startTime);
        const allSlots = Array.from(slotSet).sort((a, b) => {
          const d = toMinutes(a) - toMinutes(b);
          return d !== 0 ? d : a.localeCompare(b);
        });
        const timePages = Math.min(MAX_TIME_PAGES, Math.max(1, Math.ceil(allSlots.length / MAX_SLOTS)));
        const perPage = Math.min(MAX_SLOTS, Math.max(1, Math.ceil(allSlots.length / timePages)));
        const shown = Math.min(allSlots.length, perPage * timePages);
        for (let t = 0; t < timePages; t++) {
          const pageSlots = allSlots.slice(t * perPage, Math.min(shown, (t + 1) * perPage));
          if (!pageSlots.length) continue;
          const winSlots = new Set(pageSlots);
          // Shu vaqt oynasida darsi YO'Q sinf ustuni tushib qoladi: butunlay bo'sh
          // ustun "bu sinfda dars yo'q" degan YOLG'ON taassurot beradi (uning
          // darslari boshqa oynada ko'rinadi). Bo'sh qolgan joyni qolganlari oladi.
          const windowCols = pageCols.filter((c) => c.lessons.some((l) => winSlots.has(l.startTime)));
          schedSlides.push({
            key: `sch-${dow}-${i}-${t}`,
            kind: 'schedule',
            dow,
            isToday: dow === data.todayDow,
            cols: windowCols.length ? windowCols : pageCols,
            slots: pageSlots,
            timePages,
            // Ko'rsatkich faqat oxirgi vaqt oynasida (odatda 0)
            hiddenSlots: t === timePages - 1 ? allSlots.length - shown : 0,
            page: 0,
            pages: 0,
          });
        }
      }
      // Sahifa raqami — jadval slaydlarining UMUMIY soni bo'yicha (sinf sahifasi
      // x vaqt oynasi), shunda "3 / 6" tomoshabinga to'g'ri ma'lumot beradi.
      for (let i = 0; i < schedSlides.length; i++) {
        schedSlides[i].page = i + 1;
        schedSlides[i].pages = schedSlides.length;
      }
      out.push(...schedSlides);
    }

    /**
     * HAFTA YULDUZI. Server `null` qaytarganda (ma'lumot yo'q, 30 kunlik #1 bilan
     * ayni shaxs, yoki `KIOSK_WEEK_STAR=0`) slayd yaratilmaydi.
     */
    const star = data.weekStar;
    if (star && star.fullName) out.push({ key: 'star', kind: 'weekStar', star });

    const banners = data.banners ?? [];
    for (let i = 0; i < banners.length; i++) {
      out.push({ key: `ban-${banners[i].id}`, kind: 'banner', banner: banners[i], page: i + 1, pages: banners.length });
    }
    return out;
  }, [data]);

  // Slaydlar soni kamaysa — indeksni chegarada ushlab turamiz
  useEffect(() => {
    setIndex((i) => (slides.length ? i % slides.length : 0));
  }, [slides.length]);

  /**
   * Render vaqtidagi xavfsiz indeks: slaydlar ro'yxati ikki so'rov orasida
   * qisqarsa, useEffect ishlagunicha bir kadr bo'sh sahna ko'rinmasligi uchun.
   */
  const safeIndex = slides.length ? index % slides.length : 0;

  /**
   * Joriy slaydning davomiyligi. ATAYLAB oddiy son: `slides` ni to'g'ridan-to'g'ri
   * taymer bog'liqligiga qo'shsak, har 60 soniyalik ma'lumot yangilanishi
   * taymerni nolga qaytarardi (slayd cho'zilib ketardi).
   */
  const curMs = slides.length ? SLIDE_MS[slides[safeIndex].kind] : SLIDE_MS_DEFAULT;

  // Avto-aylanish (har slayd uchun taymer qayta ishga tushadi)
  useEffect(() => {
    if (slides.length <= 1) return;
    const id = window.setTimeout(() => setIndex((i) => (i + 1) % slides.length), curMs);
    return () => window.clearTimeout(id);
  }, [index, slides.length, curMs]);

  /**
   * QO'LDA O'TKAZISH. Ekran oldida turgan odam kutib qolmasin: o'ng strelka
   * yoki "Keyingi" yorlig'ini bosish darhol keyingi slaydga o'tkazadi.
   *
   * `setIndex` avto-taymerni ham QAYTA ISHGA TUSHIRADI: taymer `index` ga
   * bog'langan, ya'ni qo'lda o'tkazgandan keyin yangi slayd TO'LIQ vaqtini
   * oladi — yarim yo'lda almashib ketmaydi.
   */
  const surish = useCallback((yon: 1 | -1) => {
    setIndex((i) => {
      const n = slides.length;
      return n ? (i + yon + n) % n : 0;
    });
  }, [slides.length]);

  /*
   * Klaviatura. Kiosk odatda klaviatursiz ishlaydi, lekin sozlash paytida yoki
   * masofadan boshqaruv pulti ulanganda strelka yuboriladi. Probel va PageUp/
   * PageDown ham qo'shildi — ko'p pultlar aynan shularni yuboradi.
   */
  useEffect(() => {
    if (slides.length <= 1) return;
    const bos = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault();
        surish(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        surish(-1);
      }
    };
    window.addEventListener('keydown', bos);
    return () => window.removeEventListener('keydown', bos);
  }, [surish, slides.length]);


  /** Pastki paneldagi "Keyingi: …" — aeroport tablosi mantiqi. */
  const nextLabel = slides.length > 1
    ? SLIDE_LABEL[slides[(safeIndex + 1) % slides.length].kind]
    : '';

  /**
   * Slayd ichi — sekundlik soat tiklanishida qayta render bo'lmasligi uchun
   * alohida memo (element havolalari o'zgarmaydi → React ularni o'tkazib yuboradi).
   */
  const bodies = useMemo(() => slides.map((s) => {
    switch (s.kind) {
      case 'top':
        return <TopSlide items={s.items} periodDays={s.periodDays} kioskKey={kioskKey} />;
      case 'birthdays':
        return (
          <BirthdaySlide
            items={s.items} page={s.page} pages={s.pages} dateLabel={s.dateLabel} kioskKey={kioskKey}
          />
        );
      case 'leaders':
        return <LeadersSlide items={s.items} page={s.page} pages={s.pages} scale={s.scale} kioskKey={kioskKey} />;
      case 'classActivity':
        return <ClassActivitySlide items={s.items} periodDays={s.periodDays} />;
      case 'schedule':
        return (
          <ScheduleSlide
            dow={s.dow} isToday={s.isToday} cols={s.cols} slots={s.slots} timePages={s.timePages}
            hiddenSlots={s.hiddenSlots} page={s.page} pages={s.pages} nowMin={nowMin}
          />
        );
      case 'weekStar':
        return <WeekStarSlide star={s.star} kioskKey={kioskKey} />;
      case 'banner':
        return <BannerSlide banner={s.banner} page={s.page} pages={s.pages} kioskKey={kioskKey} />;
    }
  }), [slides, nowMin, kioskKey]);

  const saveKey = (e: FormEvent) => {
    e.preventDefault();
    const k = keyInput.trim();
    if (!k) { setKeyError('Kalitni kiriting'); return; }
    try { localStorage.setItem(KEY_LS, k); } catch { /* localStorage o'chirilgan */ }
    setKeyError(null);
    setKeyRejected(false);
    setKeyInput('');
    setData(null); // yangi kalit — yangi maktab, eski ma'lumot ko'rsatilmasin
    setKioskKey(k);
    setRetryNonce((n) => n + 1); // kalit o'zgarmagan bo'lsa ham darhol tekshiramiz
  };

  const goFullscreen = () => {
    void document.documentElement.requestFullscreen?.().catch(() => undefined);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  // Kalit yo'q yoki server uni rad etdi (kalit saqlanib qoladi — fonda urinamiz)
  if (!kioskKey || keyRejected) {
    return (
      <div className="sata-kiosk">
        <SetupScreen value={keyInput} error={keyError} onChange={setKeyInput} onSubmit={saveKey} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="sata-kiosk">
        <KioskHead clock={clock} offline={offline} />
        <div className="k-loading">
          <div className="k-spinner" />
          <div className="k-loading-text">{offline ? "Aloqa yo'q — qayta urinilmoqda" : 'Yuklanmoqda…'}</div>
        </div>
        <div className="k-foot">
          <div className="k-foot-right">
            {offline ? <div className="k-offline"><span className="k-offline-dot" />Aloqa yo'q</div> : null}
            <PoweredBy variant="kiosk" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sata-kiosk">
      <KioskHead school={data.school.name} clock={clock} offline={offline} />

      <div className="k-stage">
        {slides.length === 0 ? (
          <div className="k-empty">Hozircha ko'rsatiladigan ma'lumot yo'q</div>
        ) : (
          slides.map((s, i) => (
            <div key={s.key} className={`k-slide${i === safeIndex ? ' is-on' : ''}`}>
              {/*
                Provider qiymati o'zgarganda `bodies[i]` element HAVOLASI
                o'zgarmaydi — React butun slaydni o'tkazib yuboradi va faqat
                kontekstni o'qiydigan mitti hisoblagichlar qayta chiziladi.
              */}
              <SlideActiveCtx.Provider value={i === safeIndex}>
                {bodies[i]}
              </SlideActiveCtx.Provider>
            </div>
          ))
        )}
      </div>

      <div className="k-foot">
        <div className="k-dots">
          {slides.map((s, i) => <span key={s.key} className={`k-dot${i === safeIndex ? ' is-on' : ''}`} />)}
        </div>
          {/* Bosiladigan: sichqoncha, barmoq (sensorli ekran) va Enter */}
        {nextLabel ? (
          <div
            className="k-next is-clickable"
            role="button"
            tabIndex={0}
            aria-label={`Keyingi slayd: ${nextLabel}`}
            onClick={() => surish(1)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); surish(1); } }}
          >
            <IconChevronRight stroke={2.4} aria-hidden="true" />
            {/* Yorliq so'nik, SLAYD NOMI to'q — 6 metrdan avval nom o'qiladi */}
            <span className="k-next-lbl">Keyingi:</span>
            <span className="k-next-name">{nextLabel}</span>
          </div>
        ) : null}
        <div className="k-foot-right">
          {offline ? <div className="k-offline"><span className="k-offline-dot" />Aloqa yo'q</div> : null}
          <PoweredBy variant="kiosk" />
        </div>
      </div>

      {slides.length > 1 ? (
        <div className="k-progress">
          {/* `--k-slide-ms` — `prefers-reduced-motion` blokida progress MA'LUMOT
              sifatida saqlanadi (qancha vaqt qolgani), shu qiymat o'sha yerda kerak. */}
          <div
            key={`${safeIndex}-${slides.length}`}
            className="k-progress-fill"
            style={cssVars({ '--k-slide-ms': `${curMs}ms` }, { animationDuration: `${curMs}ms` })}
          />
        </div>
      ) : null}

      {!isFs ? (
        <button className="k-fs" type="button" onClick={goFullscreen} title="To'liq ekran" aria-label="To'liq ekran">
          <IconMaximize stroke={2} />
        </button>
      ) : null}
    </div>
  );
}
